import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Select, type BadgeTone } from '@artosera/ui';
import { listerTravaux, relancerTravail } from '../lib/api.js';
import { formaterDate } from '../lib/format.js';
import { ETATS_TRAVAIL } from '../lib/travaux.js';
import type { EtatTravail } from '../types.js';
import pageStyles from '../styles/page.module.css';
import { TableauDefilant } from './TableauDefilant.js';
import { useElevation } from './useElevation.js';
import tableStyles from '../styles/table.module.css';

const TONE_TRAVAIL: Record<EtatTravail, BadgeTone> = {
  en_attente: 'deposit',
  en_cours: 'reserved',
  reussi: 'available',
  echoue: 'sold',
  abandonne: 'sold',
  // Écarté parce que son module est désactivé pour la galerie (D21) : ni un
  // échec, ni relançable — il repartira quand le module sera réactivé.
  ignore_module_inactif: 'withdrawn',
};

/**
 * Seul un travail échoué ou abandonné se relance (docs/dev/api.md § 12.2).
 * `ignore_module_inactif` n'est pas un échec : pas de bouton.
 */
function estRelancable(etat: EtatTravail): boolean {
  return etat === 'echoue' || etat === 'abandonne';
}

/** Travaux par page (la même valeur que l'API). */
const TAILLE_PAGE = 50;

/** « actifs » : tout sauf les travaux ignorés (module inactif) — le choix par défaut. */
type FiltreEtat = 'actifs' | 'tous' | EtatTravail;

export function JobsTable({ galerie }: { galerie?: string }): JSX.Element {
  const { t } = useTranslation('console');
  const queryClient = useQueryClient();
  const [etat, setEtat] = useState<FiltreEtat>('actifs');
  const [type, setType] = useState('tous');
  // La page appartient à ses filtres : un autre filtre, une autre galerie, et l'on
  // repart de la première page (sans effet de bord, la clé suffit).
  const cleFiltres = `${galerie ?? ''}|${etat}|${type}`;
  const [pagination, setPagination] = useState({ cle: cleFiltres, page: 1 });
  const page = pagination.cle === cleFiltres ? pagination.page : 1;
  const allerA = (numero: number): void => setPagination({ cle: cleFiltres, page: numero });

  const requete = useQuery({
    queryKey: ['console', 'travaux', galerie ?? null, etat, type, page],
    queryFn: () =>
      listerTravaux({
        galerie,
        etat: etat === 'actifs' || etat === 'tous' ? undefined : etat,
        type: type === 'tous' ? undefined : type,
        masquerIgnores: etat === 'actifs',
        page,
      }),
    placeholderData: keepPreviousData,
  });

  // Relancer une purge ou un export demande un second facteur récent.
  const elevation = useElevation();
  const relance = useMutation({
    mutationFn: (id: string) => elevation.executer(() => relancerTravail(id)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['console', 'travaux'] });
    },
  });

  const travaux = requete.data?.items ?? [];
  const total = requete.data?.total ?? travaux.length;
  const pages = Math.max(1, Math.ceil(total / TAILLE_PAGE));
  const filtre = etat !== 'actifs' || type !== 'tous';

  const optionsEtat = [
    { value: 'actifs', label: t('travaux.etatFiltre.actifs') },
    { value: 'tous', label: t('travaux.etatFiltre.tous') },
    ...ETATS_TRAVAIL.map((valeur) => ({ value: valeur, label: t(`travaux.etatValeur.${valeur}`) })),
  ];
  const optionsType = [
    { value: 'tous', label: t('travaux.typeTous') },
    // Le type choisi reste proposé même si l'API ne le liste plus.
    ...[...new Set([...(requete.data?.types ?? []), ...(type === 'tous' ? [] : [type])])]
      .sort()
      .map((valeur) => ({ value: valeur, label: valeur })),
  ];

  const filtres = (
    <div className={tableStyles.toolbar}>
      <div className={tableStyles.champFiltre}>
        <label htmlFor={`travaux-etat-${galerie ?? 'global'}`}>{t('travaux.colonnes.etat')}</label>
        <Select
          id={`travaux-etat-${galerie ?? 'global'}`}
          options={optionsEtat}
          value={etat}
          onValueChange={(valeur) => setEtat(valeur as FiltreEtat)}
        />
      </div>
      <div className={tableStyles.champFiltre}>
        <label htmlFor={`travaux-type-${galerie ?? 'global'}`}>{t('travaux.colonnes.type')}</label>
        <Select
          id={`travaux-type-${galerie ?? 'global'}`}
          options={optionsType}
          value={type}
          onValueChange={setType}
        />
      </div>
    </div>
  );

  if (requete.isLoading) return <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>;
  if (travaux.length === 0) {
    return (
      <div>
        {filtres}
        <p className={pageStyles.videEtat}>
          {filtre || page > 1 ? t('travaux.videFiltres') : t('travaux.vide')}
        </p>
      </div>
    );
  }

  return (
    <div>
      {filtres}
      <TableauDefilant>
        <table className={tableStyles.table}>
          <thead>
            <tr>
              <th scope="col">{t('travaux.colonnes.type')}</th>
              <th scope="col">{t('travaux.colonnes.etat')}</th>
              <th scope="col">{t('travaux.colonnes.tentatives')}</th>
              <th scope="col">{t('travaux.colonnes.runAt')}</th>
              <th scope="col">{t('travaux.colonnes.erreur')}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {travaux.map((travail) => (
              <tr key={travail.id}>
                <td className={tableStyles.cellulePrincipale}>{travail.type}</td>
                <td>
                  <Badge tone={TONE_TRAVAIL[travail.etat]}>
                    {t(`travaux.etatValeur.${travail.etat}`)}
                  </Badge>
                </td>
                <td>{travail.tentatives}</td>
                <td>{formaterDate(travail.runAt, '—')}</td>
                <td className={tableStyles.celluleMuette}>
                  {travail.lastError ?? t('travaux.aucuneErreur')}
                </td>
                <td className={tableStyles.celluleActions}>
                  {estRelancable(travail.etat) ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={relance.isPending && relance.variables === travail.id}
                      onClick={() => relance.mutate(travail.id)}
                    >
                      {t('travaux.relancer')}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableauDefilant>
      <div className={tableStyles.pagination}>
        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => allerA(Math.max(1, page - 1))}
        >
          {t('travaux.pagePrecedente')}
        </Button>
        <span>{t('travaux.page', { page, pages, total })}</span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page >= pages}
          onClick={() => allerA(page + 1)}
        >
          {t('travaux.pageSuivante')}
        </Button>
      </div>
      {elevation.dialogue}
    </div>
  );
}
